import { useRouter } from "next/navigation"
import { useEffect, useRef } from "react"

// Custom hook to replace react-scroll functionality
export const useScroll = () => {
  const lenisRef = useRef<any>(null)

  useEffect(() => {
    const initLenis = async () => {
      const Lenis = (await import("lenis")).default

      const lenis = new Lenis({
        duration: 1.2,
        easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
        smoothWheel: true,
      })

      lenisRef.current = lenis

      function raf(time: number) {
        lenis.raf(time)
        requestAnimationFrame(raf)
      }

      requestAnimationFrame(raf)
    }

    initLenis()

    return () => {
      if (lenisRef.current) {
        lenisRef.current.destroy()
      }
    }
  }, [])

  const scrollTo = (
    target: string,
    options?: { duration?: number; offset?: number },
  ) => {
    const element =
      document.getElementById(target) ||
      document.querySelector(`[data-scroll-name="${target}"]`)

    if (element) {
      const offset = options?.offset || 0
      const elementPosition = element.offsetTop + offset

      if (lenisRef.current) {
        lenisRef.current.scrollTo(elementPosition, {
          duration: options?.duration || 1,
        })
      } else {
        // Fallback to native scroll if Lenis is not available
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        })
      }
    }
  }

  return { scrollTo }
}

// Hook for router-aware scrolling
export const useRouterScroll = () => {
  const router = useRouter()

  const scrollToAfterNavigation = (
    target: string,
    options?: {
      smooth?: boolean
      duration?: number
      delay?: number
      offset?: number
    },
  ) => {
    // Wait for the next tick to ensure navigation has started
    setTimeout(() => {
      const scrollToTarget = () => {
        const element =
          document.getElementById(target) ||
          document.querySelector(`[data-scroll-name="${target}"]`)

        if (element) {
          const offset = options?.offset || 0
          const elementPosition = element.offsetTop + offset

          window.scrollTo({
            top: elementPosition,
            behavior: options?.smooth !== false ? "smooth" : "auto",
          })
        }
      }

      // Wait for navigation to complete and DOM to update
      const delay = options?.delay || 100 // Default delay for navigation
      setTimeout(scrollToTarget, delay)
    }, 10)
  }

  return { scrollToAfterNavigation, router }
}

// Utility function for programmatic scrolling (replaces scroller from react-scroll)
export const scroller = {
  scrollTo: (
    target: string,
    options?: {
      smooth?: boolean
      duration?: number
      delay?: number
      offset?: number
    },
  ) => {
    const scrollToTarget = () => {
      const element =
        document.getElementById(target) ||
        document.querySelector(`[data-scroll-name="${target}"]`)

      if (element) {
        const offset = options?.offset || 0
        const elementPosition = element.offsetTop + offset

        window.scrollTo({
          top: elementPosition,
          behavior: options?.smooth ? "smooth" : "auto",
        })
      }
    }

    const delay = options?.delay || 0

    if (delay > 0) {
      setTimeout(scrollToTarget, delay)
    } else {
      // For immediate scrolling, check if we need to wait for navigation
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTarget)
      })
    }
  },
}
